<template>
  <div>
    <core-modal-header>Send Invites</core-modal-header>

    <core-modal-body >
      <u-form-group label="Connection Link">
        <template #hint>
          <core-confirm-popover :confirm-method="resetToken" confirm-text="This will permanently delete the existing Connection Link and generate a new one. Continue?">
            <u-button
              variant="outline"
              color="charcoal"
              :loading="isTokenResetting"
              size="2xs"
              icon="i-ri-reset-right-fill"
              title="Reset Connection Link"
            />
          </core-confirm-popover>
        </template>
        <p class="text-charcoal-400 text-sm mb-3 -mt-1">Use this link for instant connections, whether someone is signing up or already a member.</p>

        <div class="flex items-center gap-2">
          <core-copy-button
            :text="connectionLink"
            @click=" gtm.trackEvent({event: 'core_ui-connection_link-copy', action: 'click'});"
          />
          <core-qr-code-button
            :url="connectionLink"
            filename="connection-link"
            @click=" gtm.trackEvent({event: 'core_ui-connection_link-qrcode', action: 'click'});"
          />
        </div>
      </u-form-group>

      <u-divider class="my-6" label="or" :ui="{label: 'text-base'}" />

      <u-form @submit="submit" :schema="schema" :state="state" :validate-on="['submit']">
        <u-form-group label="Email Addresses (separated by comma)" name="to_email" class="mb-4" required>
          <u-input v-model="state.to_email" placeholder="Emails to invite" />
        </u-form-group>

        <div class="flex gap-2 justify-end">
          <u-button variant="soft" color="charcoal" @click="modal.close()">Cancel</u-button>
          <u-button type="submit" :loading="isSaving">Send Emails</u-button>
        </div>
      </u-form>
    </core-modal-body>
  </div>
</template>

<script setup>
  import {object, string} from 'yup';

  const props = defineProps({
    modal: Object
  });

  const isTokenResetting = ref(false);
  const connectionToken = ref(null);
  const gtm = useGtm();

  async function refreshConnectionToken() {
    try {
      const user = await useSessionApi().getCurrentUser();
      connectionToken.value = user?.connection_token || null;
    } catch (e) {
      connectionToken.value = null;
    }
  }

  async function resetToken() {
    try {
      isTokenResetting.value = true;
      await useSessionApi().resetConnectionToken();
      await useSessionStore().getCurrentUser();
      await refreshConnectionToken();

      gtm.trackEvent({event: 'core_ui-connection_link-reset', action: 'click'});

      useSuccessToast().add({
        id: 'reset-success',
        description: 'Your Connection Link was reset.'
      })
    } catch (e) {
      console.log(e)
    } finally {
      isTokenResetting.value = false;
    }
  }

  const {currentUser} = storeToRefs(useSessionStore());
  const connectionLink = computed(() => {
    if (!currentUser.value?.slug || !connectionToken.value) {
      return '';
    }

    const base = document.location.origin;
    const route = buildRouteParams(SHARE_ROUTES.profileConnectionLink, {
      userId: currentUser.value.slug,
      token: connectionToken.value
    });
    return `${base}${route}`;
  });

  onMounted(refreshConnectionToken);

  const isSaving = ref(false);
  const schema = object({
    to_email: string().required('At least one email is required').test('max-emails', 'A maximum of 5 email addresses is allowed.', value => value.split(/[,;\s]+/).filter(e => e.trim()).length < 6)
  });

  const state = reactive({
    to_email: ''
  });

  async function submit() {
    try {
      isSaving.value = true;

      await useUserApi().inviteByEmail({
        to_email: state.to_email.split(/[,;\s]+/).map(e => e.trim()).filter(e => e)
      });

      useSuccessToast().add({
        id: 'connection-add',
        title: `Invite Sent`,
        description: `Your invite has been sent.`
      });
      props.modal.close();
    } catch (e) {
      useErrorToast().add();
    } finally {
      isSaving.value = false;
    }
  }
</script>
